import React, {useCallback, useEffect, useState} from "react";
import {Col, Row, Select} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {CompanyFilter, SubCompanyFilter} from "components/_defaultComponents/FilterComponents";
import {TICKET_PRIORITIES, TICKET_STATUSES} from "constants/ticket";
import {DebouncedInput} from "./DebouncedInput";

const searchPlaceholderKeys = {
    courier: "searchCourier",
    company: "searchCompany",
    onboarding_company: "searchOnboarding",
    onboarding_agency: "searchOnboarding",
    other: "searchOther"
};

export const TicketFilters = ({category, filters, roleOptions, onChange, extra}) => {
    const {t} = useTranslation(["tickets"]);
    const stores = useStore();
    const [assignableUsers, setAssignableUsers] = useState([]);

    useEffect(() => {
        let isCurrent = true;
        setAssignableUsers([]);
        if (filters.role) {
            stores.ticketStore.getAssignableUsers(filters.role).then(response => {
                if (isCurrent) {
                    setAssignableUsers(response?.data?.users || []);
                }
            });
        }
        return () => {
            isCurrent = false;
        };
    }, [filters.role]);

    const handleNumberChange = useCallback(value => onChange({number: value}), [onChange]);
    const handleSearchChange = useCallback(value => onChange({search: value}), [onChange]);

    const select = (key, placeholder, value, options, extraProps = {}) => (
        <Select
            allowClear
            placeholder={placeholder}
            value={value || undefined}
            onChange={newValue => onChange({[key]: newValue || ""})}
            style={{width: "100%"}}
            {...extraProps}
        >
            {options}
        </Select>
    );

    return (
        <Row gutter={[16, 16]} style={{marginBottom: 16}} align="middle">
            <Col xs={24} sm={12} md={6}>
                <DebouncedInput
                    value={filters.number}
                    placeholder={t("list.filters.number", {ns: "tickets"})}
                    onChange={handleNumberChange}
                />
            </Col>
            <Col xs={24} sm={12} md={6}>
                <DebouncedInput
                    value={filters.search}
                    placeholder={t(`list.filters.${searchPlaceholderKeys[category]}`, {
                        ns: "tickets"
                    })}
                    onChange={handleSearchChange}
                />
            </Col>
            {category === "company" && stores.meStore.checkRole("ticketCompanyFilter") && (
                <>
                    <Col xs={24} sm={12} md={6}>
                        <CompanyFilter
                            hideTitle
                            showPoolOption={false}
                            selectMarginBottom="0px"
                            placeholder={t("list.filters.company", {ns: "tickets"})}
                            value={filters.companyId || undefined}
                            onChange={value => onChange({companyId: value || "", subCompanyId: ""})}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <SubCompanyFilter
                            hideTitle
                            allowClear
                            showSearch
                            showAllOption={false}
                            selectMarginBottom="0px"
                            initialCall={!!filters.companyId}
                            company={filters.companyId || undefined}
                            disabled={!filters.companyId}
                            placeholder={t("list.filters.subCompany", {ns: "tickets"})}
                            value={filters.subCompanyId || undefined}
                            onChange={value => onChange({subCompanyId: value || ""})}
                        />
                    </Col>
                </>
            )}
            <Col xs={24} sm={12} md={6}>
                {select(
                    "role",
                    t("list.filters.allRoles", {ns: "tickets"}),
                    filters.role,
                    roleOptions.map(role => (
                        <Select.Option key={role.key} value={role.key}>
                            {role.name}
                        </Select.Option>
                    )),
                    {onChange: value => onChange({role: value || "", assignedUserId: ""})}
                )}
            </Col>
            <Col xs={24} sm={12} md={6}>
                {select(
                    "assignedUserId",
                    filters.role
                        ? t("list.filters.assignedUser", {ns: "tickets"})
                        : t("list.filters.selectRoleFirst", {ns: "tickets"}),
                    filters.assignedUserId,
                    assignableUsers.map(user => (
                        <Select.Option key={user._id} value={user._id}>
                            {user.fullName}
                        </Select.Option>
                    )),
                    {disabled: !filters.role, showSearch: true, optionFilterProp: "children"}
                )}
            </Col>
            <Col xs={24} sm={12} md={6}>
                {select(
                    "priority",
                    t("list.filters.allPriorities", {ns: "tickets"}),
                    filters.priority,
                    TICKET_PRIORITIES.map(priority => (
                        <Select.Option key={priority} value={priority}>
                            {t(`priorities.${priority}`, {ns: "tickets"})}
                        </Select.Option>
                    ))
                )}
            </Col>
            <Col xs={24} sm={12} md={6}>
                {select(
                    "status",
                    t("list.filters.allStatuses", {ns: "tickets"}),
                    filters.status,
                    TICKET_STATUSES.map(status => (
                        <Select.Option key={status} value={status}>
                            {t(`statuses.${status}`, {ns: "tickets"})}
                        </Select.Option>
                    ))
                )}
            </Col>
            {extra && (
                <Col flex="auto" style={{textAlign: "right"}}>
                    {extra}
                </Col>
            )}
        </Row>
    );
};
